import { useTaktikalAuthPoll, useBaseTaktikalAuthStart } from '@/api/react-query'
import { useI18n } from '@/i18n/i18n-react'
import { useAuth } from '@/providers/auth-provider'
import { Sentry } from '@/utils/error-handling'
import { useGetSubject, useAsyncEffect } from '@/utils/hooks'
import { StoredObjectSubject } from '@/utils/storage'
import { isValidSsn, normalizePhoneNumber, normalizeSsn } from '@/utils/taktikal'
import { useEffect, useState } from 'react'

type TaktikalAuthState = {
	authRequestUuid: string
	verificationCode: string
	startedAt: number
}

const taktikalAuthStateSubject = new StoredObjectSubject<TaktikalAuthState | null>('taktikalAuthState', null)
const AUTH_TIMEOUT_MS = 5 * 60 * 1000

export function useTaktikalAuth(options: { authMethod: 'phone' | 'ssn'; value: string; onSuccess: () => void }) {
	const { authMethod, value } = options
	const { L } = useI18n()
	const { logIn } = useAuth()
	const [error, setError] = useState<string | null>(null)

	const authState = useGetSubject(taktikalAuthStateSubject)
	const authRequestUuid = authState?.authRequestUuid
	const verificationCode = authState?.verificationCode
	const isTimedOut = authState ? Date.now() - authState.startedAt > AUTH_TIMEOUT_MS : false

	const startAuth = useBaseTaktikalAuthStart(true, {
		onSuccess: data => {
			if (data.isSuccess) {
				setError(null)
				taktikalAuthStateSubject.next({
					authRequestUuid: data.response.authRequestUuid,
					verificationCode: data.response.verificationCode,
					startedAt: Date.now()
				})
			} else {
				setError(L.Errors.Generic())
			}
		},
		onError: () => {
			setError(L.Errors.Generic())
		}
	})

	const pollAuth = useTaktikalAuthPoll(
		{ authRequestUuid: authRequestUuid ?? '' },
		{
			enabled: !!authRequestUuid && !isTimedOut,
			refetchInterval: 1000,
			retry: false
		}
	)

	useAsyncEffect(async () => {
		if (!authRequestUuid || !pollAuth.data) {
			return
		}

		if (pollAuth.data.status === 'FAILED') {
			taktikalAuthStateSubject.next(null)
			setError(L.Errors.Generic())
			return
		}

		if (pollAuth.data.status !== 'COMPLETED') {
			return
		}

		if (pollAuth.data.token) {
			logIn(pollAuth.data.token, pollAuth.data.customer)
		}

		taktikalAuthStateSubject.next(null)
		setError(null)
		options.onSuccess()
	}, [authRequestUuid, pollAuth.data])

	useEffect(() => {
		if (!authRequestUuid || !pollAuth.error) {
			return
		}
		Sentry.captureException(pollAuth.error)
		taktikalAuthStateSubject.next(null)
		setError(L.Errors.Generic())
	}, [authRequestUuid, pollAuth.error, L])

	useEffect(() => {
		if (isTimedOut && authRequestUuid) {
			taktikalAuthStateSubject.next(null)
			setError(L.Auth.AuthenticationTimedOut())
		}
	}, [isTimedOut, authRequestUuid, L])

	const handleSignIn = () => {
		const cleanValue = value.trim()
		if (!cleanValue || startAuth.isPending || (authRequestUuid && !isTimedOut)) {
			return
		}
		setError(null)

		const normalizedValue = authMethod === 'phone' ? normalizePhoneNumber(cleanValue) : normalizeSsn(cleanValue)

		if (authMethod === 'ssn' && !isValidSsn(normalizedValue)) {
			setError(L.Errors.Generic())
			return
		}

		startAuth.mutate({
			body: {
				...(authMethod === 'phone' ? { phoneNumber: normalizedValue } : {}),
				...(authMethod === 'ssn' ? { ssn: normalizedValue } : {})
			}
		})
	}

	return {
		verificationCode,
		isAuthenticating: !!authRequestUuid,
		isTimedOut,
		isPending: startAuth.isPending,
		error,
		handleSignIn
	}
}
